import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import style from "./export-dialog.module.css";
import cn from "classnames";
import { minmax } from "frontend/geometry/utils";
import { range, EventFor } from "frontend/utils/fn-utils";
import Checkbox from "frontend/ui-components/checkbox";
import StyledButton, { ButtonSize } from "frontend/ui-components/styled-button";
import AppLoader from "frontend/loader/app-loader";
import { getPathPrefix } from "../../utils/getPathPrefix";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import { PlanFeatureName } from "shared/datamodel/schemas";

const upgradeIconPath = getPathPrefix("/images/golden-crown.svg");
const UpgradeIcon = <img src={upgradeIconPath} width="16px" alt={"upgrade"} draggable={false} />;

function ModalDialog({
  onClose,
  children,
  style: extraStyle,
}: {
  onClose: () => void;
  children?: React.ReactNode;
  style?: CSSProperties;
}) {
  // not using useOutsideRef here on purpose
  // This dialog opens another (the upgrade dialog) and I want this to stay
  // open even when user interacts with that dialog.
  //
  // useOutsideRef doesn't support this use case (nested dialogs) yet

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const stopEvent = (e: any) => {
    if (e.target == e.currentTarget) {
      e.stopPropagation();
      // e.preventDefault(); // wheel event is passive by default, can't prevent it
    }
    if (e.target == e.currentTarget) {
      e.stopPropagation();
      // e.preventDefault(); // wheel event is passive by default, can't prevent it
    }
  };

  return (
    <div
      className={cn(style.newModalBg, style.animateFadeIn)}
      onMouseDown={(e) => {
        if (e.target == e.currentTarget) onClose(); // close dialog when clicking on background
      }}
      onScroll={stopEvent} // stop scroll on background
      onWheel={stopEvent}
    >
      <div role="dialog" className={style.newModal} style={extraStyle}>
        {children}
      </div>
    </div>
  );
}

interface Frame {
  id: string;
  name: string;
}

type Quality = "low" | "high" | "ultra";

export type ExportConfiguration = {
  format: "image" | "pdf";
  quality: Quality;
  singlePage: boolean;
  frames: Frame[];
  whiteBackground: boolean;
};

export function ExportDialog({
  getFramesFn,
  close,
  onExport,
  onUpgrade,
}: {
  getFramesFn?: () => Frame[];
  close: () => void;
  onExport: (withWatermark: boolean, config: ExportConfiguration) => void;
  onUpgrade: () => void;
}) {
  const premiumExportEnabled = usePlanFeatureValue(PlanFeatureName.PREMIUM_EXPORT);
  const frames = getFramesFn ? getFramesFn() : [];
  const framesIds = frames.map((f) => f.id);

  const [format, setFormat] = useState<"image" | "pdf">("image");
  const [quality, setQuality] = useState<Quality>("low");
  const [what, setWhat] = useState<"all" | "frames">("all");
  const [framesToExport, setFramesToExport] = useState<string[]>(framesIds);
  const [isProcessing, setIsProcessing] = useState(false);

  const upgradeIcon = !premiumExportEnabled && UpgradeIcon;

  function onFormatChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value === "png") {
      setFormat("image");
    } else if (event.target.value === "pdf") {
      if (premiumExportEnabled) {
        setFormat("pdf");
      } else {
        event.preventDefault();
        onUpgrade();
      }
    } else {
      console.error("unhandled format value", event.target.value);
    }
  }

  function onQualityChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value != "low" && !premiumExportEnabled) {
      event.preventDefault();
      onUpgrade();
      return;
    }
    setQuality(event.target.value as Quality);
  }

  function handleSubmit(e: EventFor<"button", "onClick">) {
    e.preventDefault();
    setIsProcessing(true);
    const config = {
      format,
      quality,
      singlePage: what == "all",
      frames: frames.filter((f) => framesToExport.includes(f.id)),
      whiteBackground: true,
    };
    setTimeout(() => onExport(!premiumExportEnabled, config), 10);
  }

  const processingMessage = isProcessing && (
    <div className={style.overlay}>
      <AppLoader />
      <div className={style["h3"]}>Exporting...</div>
    </div>
  );

  return (
    <ModalDialog onClose={close}>
      <div className={style["export-modal-main"]}>
        <div className={style["left-pane-image"]}>
          <img
            draggable={false}
            width={263}
            height={234}
            src={getPathPrefix("/images/export-modal-image.png")}
            alt={""}
          />
        </div>
        <div className={style["right-pane"]}>
          <h1>Export this canvas</h1>
          <h2>File format</h2>
          <div className={style.stack}>
            <label className={style["custom-radio"]}>
              <input type="radio" name="format" value="png" onChange={onFormatChange} checked={format === "image"} />
              PNG
            </label>
            <label className={style["custom-radio"]}>
              <input type="radio" name="format" value="pdf" onChange={onFormatChange} checked={format === "pdf"} />
              {upgradeIcon}
              PDF
            </label>
          </div>
          <hr />
          {format === "image" && (
            <section className={style.options} id="png-options">
              <h2>Quality</h2>
              <div className={style.stack}>
                <div className={style.row}>
                  <label className={style["custom-radio"]}>
                    <input
                      type="radio"
                      name="quality"
                      value="low"
                      checked={quality == "low"}
                      onChange={onQualityChange}
                    />
                    Low
                  </label>
                  <label className={style["custom-radio"]}>
                    <input
                      type="radio"
                      name="quality"
                      value="high"
                      checked={quality == "high"}
                      onChange={onQualityChange}
                    />
                    {upgradeIcon}High
                  </label>
                  <label className={style["custom-radio"]}>
                    <input
                      type="radio"
                      name="quality"
                      value="ultra"
                      checked={quality == "ultra"}
                      onChange={onQualityChange}
                    />
                    {upgradeIcon}Ultra
                  </label>
                </div>
              </div>
              <br />
              {!premiumExportEnabled && (
                <div className={style.row2} onClick={onUpgrade} data-testid="remove-watermark-button">
                  <label className={style["custom-checkbox"]}>
                    <input type="checkbox" checked={false} onClick={onUpgrade} />
                    <span className={style["checkbox-control"]} />
                    {upgradeIcon}
                    Remove watermark
                  </label>
                </div>
              )}
            </section>
          )}

          {format == "pdf" && (
            <section className={style.options} id="pdf-options">
              <h2>What to export</h2>
              <div className={style.stack}>
                <div className={style.row}>
                  <label
                    className={cn(style["custom-radio"], style.dontgrow, frames.length === 0 && style.disabledItem)}
                  >
                    <input
                      type="radio"
                      name="what"
                      value="frames"
                      checked={what == "frames"}
                      onChange={() => setWhat("frames")}
                      disabled={frames.length === 0}
                    />
                    Specific frames
                  </label>
                  <SelectFromNumberedList
                    optionsIds={framesIds}
                    selectedIds={framesToExport}
                    disabled={what == "all"}
                    itemRenderer={function (id: string, isSelected: boolean, index: number): React.ReactNode {
                      return (
                        <div className={style.frameItemStyle}>
                          <span>
                            {id == "__all__" ? "All" : `${index + 1}. ${frames.find((f) => f.id == id)?.name}`}
                          </span>
                          <Checkbox
                            checked={isSelected}
                            customStyle={{ transform: "scale(0.72727272)", flex: "0 0 auto" }}
                          />
                        </div>
                      );
                    }}
                    onChange={(frames) => setFramesToExport(frames)}
                  />
                </div>
                <label className={style["custom-radio"]}>
                  <input type="radio" name="what" value="all" checked={what == "all"} onChange={() => setWhat("all")} />
                  Entire canvas
                </label>
              </div>
            </section>
          )}
          <StyledButton
            title="Export"
            size={ButtonSize.big}
            enabled={true}
            onClick={handleSubmit}
            customStyle={{ float: "inline-end", fontSize: "16px", width: "150px", height: "38px" }}
          />
        </div>
      </div>
      <button className={style.closeButton} onClick={close} data-testid="close-export-button" />
      {processingMessage}
    </ModalDialog>
  );
}

function SelectFromNumberedList({
  optionsIds,
  selectedIds,
  itemRenderer,
  classNameForItemsContainer,
  onChange,
  allowSelectAll = true,
  disabled = false,
}: {
  optionsIds: string[];
  selectedIds: string[];
  itemRenderer: (optionId: string, isSelected: boolean, index: number) => React.ReactNode;
  classNameForItemsContainer?: string;
  onChange: (selection: string[]) => void;
  allowSelectAll?: boolean;
  disabled?: boolean;
}) {
  const inputid = useMemo(() => `input${Math.random().toFixed(5)}`, []); // useId isn't in our React version ?!

  function selectionToText(ids?: string[]) {
    ids ??= selectedIds;
    const indexes = ids.map((id) => optionsIds.indexOf(id) + 1);
    indexes.sort((a, b) => a - b);
    let startRange = 0,
      current = 1,
      texts = [];
    while (current < indexes.length) {
      // find the longest sub-sequence of consecutive numbers
      while (current < indexes.length && indexes[current] == indexes[current - 1] + 1) {
        current++;
      }
      if (current - startRange > 1) {
        texts.push(`${indexes[startRange]}-${indexes[current - 1]}`);
      } else {
        texts.push(`${indexes[startRange]}`);
      }
      startRange = current;
      current++;
    }
    if (current - startRange > 1) {
      texts.push(`${indexes[startRange]}-${indexes[current - 1]}`);
    } else if (startRange < indexes.length) {
      texts.push(`${indexes[startRange]}`);
    }
    return texts.join(", ");
  }

  function setSelectedIds(newSelectedIds: string[], updateInput: boolean) {
    onChange(newSelectedIds);
    if (updateInput) {
      const input = document.getElementById(inputid) as HTMLInputElement;
      if (input) {
        input.value = selectionToText(newSelectedIds);
      }
    }
  }

  function parseText(event: EventFor<"input", "onChange">) {
    const input = event.currentTarget.value.trim();
    if (input.length === 0) {
      setSelectedIds([], false);
      return;
    }
    const terms = input.split(/,|\s+/g); // split input on commas or spaces
    let newFrames: number[] = [];
    let parsingFailed = false;
    for (const term of terms) {
      if (/^\d+$/.test(term)) {
        newFrames.push(Number(term));
      } else if (/^\d+-\d+$/.test(term)) {
        // split to 2 numbers, clamp them to our range, add them to the frames list
        let [start, end] = term.split("-").map(Number);
        [start, end] = minmax(start, end);
        start = Math.max(1, start);
        end = Math.min(optionsIds.length, end);
        for (let index = start; index <= end; index++) {
          newFrames.push(index);
        }
      } else if (/all/i.test(term)) {
        newFrames = range(1, optionsIds.length).toArray();
      } else {
        parsingFailed = true;
      }
    }
    if (!parsingFailed) {
      setSelectedIds(
        optionsIds.filter((_, index) => newFrames.includes(index + 1)),
        false
      );
    }
  }

  function selectAll(event_: EventFor<"li", "onMouseDown">) {
    event_.preventDefault(); // must stop event, otherwise the parent element loses focus and dropdown closes
    setSelectedIds(selectedIds.length == optionsIds.length ? [] : optionsIds, true);
  }

  const onItemClick = (id: string) => (event: EventFor<"li", "onMouseDown">) => {
    event.preventDefault(); // must stop event, otherwise the parent element loses focus and dropdown closes
    const newSelected = selectedIds.includes(id) ? selectedIds.filter((x) => x != id) : [...selectedIds, id];
    setSelectedIds(newSelected, true);
  };

  return (
    <div className={style.selectParent}>
      <input
        type="text"
        id={inputid}
        disabled={disabled}
        onChange={parseText}
        onBlur={(e) => (e.currentTarget.value = selectionToText())}
        defaultValue={selectionToText()}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          color: disabled ? "#D0D0D9" : "var(--text)",
          outline: "none",
          border: "none",
          textIndent: "12px",
          lineHeight: "33px",
          userSelect: disabled ? "none" : "auto",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      {!disabled && (
        <div className={cn(style.selectDropdown, classNameForItemsContainer)}>
          {allowSelectAll && (
            <li key="__all__" onMouseDown={selectAll}>
              {itemRenderer("__all__", selectedIds.length == optionsIds.length, 0)}
            </li>
          )}
          {optionsIds.map((id, index) => (
            <li key={id} onMouseDown={onItemClick(id)}>
              {itemRenderer(id, selectedIds.includes(id), index)}
            </li>
          ))}
        </div>
      )}
    </div>
  );
}
